import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { logout } from '../../store/authSlice';
import api from '../../api/axios';
import NotificationDropdown from './NotificationDropdown';

interface LayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  name: string;
  path: string;
  icon: React.ReactNode;
  adminOnly?: boolean;
  employeeOnly?: boolean;
  requirePermission?: {
    canManageUsers?: boolean;
    canManageDepartments?: boolean;
    canApproveProjects?: boolean;
    canViewAllProjects?: boolean;
    canViewAllEvaluations?: boolean;
    canManageOrganisations?: boolean;
  };
  minPriority?: number;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarHover, setSidebarHover] = useState(false);
  const [userSP, setUserSP] = useState<number>(0);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  // Fetch user's story points from UserStoryPoints (source of truth)
  useEffect(() => {
    if (user) {
      const userId = user.id || user._id;
      if (userId) {
        api.get(`/story-points/user/${userId}/profile`)
          .then(res => {
            const d = res.data?.data;
            const today = new Date();
            const isLastDay = today.getDate() === new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
            setUserSP(isLastDay ? (d?.netPoints ?? 0) : (d?.currentMonthEarned ?? 0));
          })
          .catch(() => {});
      }
    }
  }, [user]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const isLegacyAdmin = user?.role === 'admin';
  const userPriority = user?.organizationRole?.priority ?? 0;
  const isAdmin = isLegacyAdmin || userPriority >= 50;

  const hasPermission = (item: NavItem): boolean => {
    // canManageOrganisations is never granted by isLegacyAdmin alone —
    // it must be explicitly present on the user (backend-computed).
    if (item.requirePermission?.canManageOrganisations) {
      return !!user?.canManageOrganisations;
    }

    if (isLegacyAdmin) return true;

    const orgRole = user?.organizationRole;

    if (item.minPriority !== undefined && userPriority < item.minPriority) {
      return false;
    }

    if (item.requirePermission) {
      if (item.requirePermission.canManageUsers && !orgRole?.canManageUsers) return false;
      if (item.requirePermission.canManageDepartments && !orgRole?.canManageDepartments) return false;
      if (item.requirePermission.canApproveProjects && !orgRole?.canApproveProjects) return false;
      if (item.requirePermission.canViewAllProjects && !orgRole?.canViewAllProjects) return false;
      if (item.requirePermission.canViewAllEvaluations && !orgRole?.canViewAllEvaluations) return false;
    }

    return true;
  };

  const isActive = (path: string) => {
    if (path === '/admin/dashboard' || path === '/employee/dashboard') {
      return location.pathname === path;
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // --- All nav items with full permission metadata ---
  const allNavItems: NavItem[] = [
    // === Common items (visible to all) ===
    {
      name: 'Dashboard',
      path: isAdmin ? '/admin/dashboard' : '/employee/dashboard',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955a1.126 1.126 0 011.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
        </svg>
      ),
    },
    // Projects — admin only (employees access projects via dashboard workspace)
    {
      name: 'Projects',
      path: '/admin/workspace',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
        </svg>
      ),
      minPriority: 50,
    },
    {
      name: 'Knowledge Base',
      path: '/knowledge-base',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
        </svg>
      ),
    },
    // === Board (employee only — admin accesses via dashboard/management tabs) ===
    {
      name: 'Board',
      path: '/employee/board',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
        </svg>
      ),
      employeeOnly: true,
    },
    // === Employee-only items ===
    {
      name: 'Evaluations',
      path: '/employee/evaluations',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
        </svg>
      ),
      employeeOnly: true,
    },
    // Project Board — moved to Management > Board tab
    // {
    //   name: 'Project Board',
    //   path: '/admin/management?tab=board',
    //   icon: (
    //     <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    //       <path strokeLinecap="round" strokeLinejoin="round" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
    //     </svg>
    //   ),
    // },
    // === Admin: Management (unified) ===
    {
      name: 'Management',
      path: '/admin/management',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
        </svg>
      ),
      minPriority: 50,
    },
    {
      name: 'Approvals',
      path: '/approvals',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      minPriority: 50,
    },
    // === Admin: Evaluations ===
    {
      name: 'Evaluations',
      path: '/admin/evaluations',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0118 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3l1.5 1.5 3-3.75" />
        </svg>
      ),
      minPriority: 50,
    },
    // === Admin: People (unified) ===
    {
      name: 'People',
      path: '/admin/people',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
        </svg>
      ),
      minPriority: 50,
    },

    // === Admin: Organisations ===
    {
      name: 'Organisations',
      path: '/admin/organisations',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
        </svg>
      ),
      requirePermission: { canManageOrganisations: true },
    },

    // === Admin: Config (evaluation setup) ===
    {
      name: 'Config',
      path: '/admin/config',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
        </svg>
      ),
      minPriority: 90,
    },
  ];

  // Apply the same filtering logic as before
  const filteredNavItems = allNavItems.filter(item => {
    if (item.adminOnly && !isLegacyAdmin) return false;
    if (item.employeeOnly && isAdmin) return false;
    if (item.requirePermission || item.minPriority !== undefined) {
      if (!hasPermission(item)) return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-900 bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Far-left Icon Bar — expands on hover to show labels */}
      <aside
        onMouseEnter={() => setSidebarHover(true)}
        onMouseLeave={() => setSidebarHover(false)}
        className={`fixed top-0 left-2 bottom-0 bg-gray-900 flex flex-col z-50 transition-all duration-200 ease-in-out lg:translate-x-0 ${
          sidebarHover ? 'w-48' : 'w-14'
        } ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        {/* Logo — sits at the very top, aligned with top bar height */}
        <div className="h-14 flex items-center px-3 shrink-0">
          <button onClick={() => navigate(isAdmin ? '/admin/dashboard' : '/employee/dashboard')} className="w-8 h-8 rounded-lg overflow-hidden hover:opacity-80 transition-opacity shrink-0" title="Home">
            <img src="https://littleimages.blob.core.windows.net/movieproviders/movies//7ED6F383-9147-4126-96EC-51F22B6DB290" alt="Logo" className="w-full h-full object-cover" />
          </button>
          {sidebarHover && <span className="ml-2.5 text-sm font-semibold text-white truncate">Little Talent</span>}
        </div>

        {/* Nav icons + labels */}
        <div className="flex-1 flex flex-col gap-1 overflow-y-auto px-1.5 pt-1">
          {filteredNavItems.map(item => (
            <button
              key={item.path}
              onClick={() => {
                navigate(item.path);
                setSidebarOpen(false);
              }}
              title={sidebarHover ? undefined : item.name}
              className={`h-10 rounded-xl flex items-center gap-2.5 px-2.5 transition-colors flex-shrink-0 ${
                isActive(item.path)
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:bg-gray-800 hover:text-white'
              }`}
            >
              <span className="shrink-0">{item.icon}</span>
              {sidebarHover && <span className="text-sm font-medium truncate">{item.name}</span>}
            </button>
          ))}
        </div>

        {/* Bottom: settings, avatar, logout */}
        <div className="flex flex-col gap-1 flex-shrink-0 pt-2 border-t border-gray-700/50 mx-2 pb-2">
          {/* Settings */}
          <button
            onClick={() => {
              navigate('/settings');
              setSidebarOpen(false);
            }}
            title={sidebarHover ? undefined : 'Settings'}
            className={`h-10 rounded-xl flex items-center gap-2.5 px-2.5 transition-colors ${
              isActive('/settings')
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:bg-gray-800 hover:text-white'
            }`}
          >
            <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {sidebarHover && <span className="text-sm font-medium truncate">Settings</span>}
          </button>

          {/* User avatar */}
          <div
            className="h-10 rounded-xl flex items-center gap-2.5 px-2.5 transition-colors text-gray-400"
          >
            <div className="w-7 h-7 bg-blue-500 rounded-full flex items-center justify-center shrink-0">
              <span className="text-white font-semibold text-xs">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            {sidebarHover && (
              <div className="min-w-0">
                <p className="text-xs font-medium text-gray-200 truncate leading-none">{user?.name || 'User'}</p>
                <p className="text-[10px] text-gray-500 truncate leading-none mt-0.5">{user?.organizationRole?.name || user?.role || 'User'}</p>
              </div>
            )}
          </div>

          {/* Logout */}
          <button
            onClick={handleLogout}
            title={sidebarHover ? undefined : 'Logout'}
            className="h-10 rounded-xl flex items-center gap-2.5 px-2.5 transition-colors text-gray-400 hover:bg-red-600 hover:text-white"
          >
            <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
            </svg>
            {sidebarHover && <span className="text-sm font-medium truncate">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Top Bar */}
      <header className="fixed top-0 left-0 lg:left-[4.5rem] right-0 h-14 bg-white border-b border-gray-200 flex items-center px-4 z-40">
        {/* Left: hamburger (mobile) + page title */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          </button>
          <h2 className="text-sm font-semibold text-gray-900 hidden sm:block">
            {filteredNavItems.find(item => isActive(item.path))?.name || 'Little Talent'}
          </h2>
        </div>

        {/* Center: Search bar */}
        <div className="flex-1 flex justify-center px-4">
          <div className="w-full max-w-md">
            <div className="bg-gray-100 rounded-lg px-4 py-2 flex items-center gap-2 cursor-pointer hover:bg-gray-200 transition-colors">
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              <span className="text-sm text-gray-400 select-none">Search...</span>
              <span className="text-xs text-gray-400 bg-gray-200 rounded px-1.5 py-0.5 ml-auto hidden sm:inline-block">Ctrl+K</span>
            </div>
          </div>
        </div>

        {/* Right section */}
        <div className="flex items-center gap-2">
          {/* SP counter badge */}
          <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium hidden sm:inline-block">
            SP: {userSP}
          </span>

          {/* Notification bell */}
          <NotificationDropdown />

          {/* Settings gear */}
          <button
            onClick={() => navigate('/settings')}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors hidden sm:flex"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>

          {/* User avatar dropdown */}
          <div className="flex items-center gap-2 pl-2 border-l border-gray-200">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center cursor-pointer" title={user?.name || 'User'}>
              <span className="text-white font-semibold text-xs">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            <div className="hidden md:block">
              <p className="text-xs font-medium text-gray-900 leading-none">{user?.name}</p>
              <p className="text-xs text-gray-500 leading-none mt-0.5">
                {user?.organizationRole?.name || user?.role || 'User'}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-[4.5rem] mt-14 p-2 lg:p-3">
        {children}
      </main>
    </div>
  );
};

export default Layout;
